import type {FriendData, ProfileSummary} from '../types/steam';

/**
 * Calculate the number of full years between two dates.
 * Uses proper date arithmetic instead of approximating with 365.25 days.
 * 
 * @param fromDate - The earlier date
 * @param toDate - The later date (defaults to current date)
 * @returns The number of full years that have passed
 */
export function getYearsBetween(fromDate: Date, toDate: Date = new Date()): number {
    let years = toDate.getFullYear() - fromDate.getFullYear();
    
    // Check if the birthday hasn't occurred yet this year
    const monthDiff = toDate.getMonth() - fromDate.getMonth();
    const dayDiff = toDate.getDate() - fromDate.getDate();
    
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        years--;
    }
    
    return years;
}

function getMonthsBetween(fromDate: Date, toDate: Date = new Date()): number {
    let months = (toDate.getFullYear() - fromDate.getFullYear()) * 12 + (toDate.getMonth() - fromDate.getMonth());

    if (toDate.getDate() < fromDate.getDate()) {
        months--;
    }

    return months;
}

export function getAccountAgeLabel(fromDate: Date, toDate: Date = new Date()): string {
    const months = getMonthsBetween(fromDate, toDate);

    if (months < 1) {
        return '<1 month';
    }

    if (months < 12) {
        return `${months} month${months !== 1 ? 's' : ''}`;
    }

    const years = Math.round(months / 12);
    return `${years} year${years !== 1 ? 's' : ''}`;
}

export function normalizeRecentNames(recentNames: string[], currentName: string): string[] {
    return recentNames
        .map((name) => name.trim())
        .filter((name, index, names) =>
            name.length > 0 &&
            name.toLowerCase() !== currentName.toLowerCase() &&
            names.findIndex((entry) => entry.toLowerCase() === name.toLowerCase()) === index,
        );
}

/**
 * Format a Unix timestamp as a date with age in years.
 * 
 * @param timestamp - Unix timestamp in seconds
 * @returns Formatted string like "Jan 15, 2020 (4 years)"
 */
export function formatDateWithAge(timestamp: number, toDate: Date = new Date()): string {
    const date = new Date(timestamp * 1000);
    const ageLabel = getAccountAgeLabel(date, toDate);
    const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
    return `${formattedDate} (${ageLabel})`;
}

export type SmurfMatchType = 'direct' | 'recent' | null;

export interface SmurfMatch {
    type: Exclude<SmurfMatchType, null>;
    strength: number;
}

/**
 * Check if two names are similar enough to be considered potential smurfs.
 * Compares names case-insensitively and checks for exact matches or variants.
 * 
 * @param name1 - First name to compare
 * @param name2 - Second name to compare
 * @returns true if names match or are similar variants
 */
function getNameSimilarity(name1: string, name2: string): number {
    if (!name1 || !name2) {
        return 0;
    }
    
    const normalize = (name: string) => name.toLowerCase().trim();
    const n1 = normalize(name1);
    const n2 = normalize(name2);
    
    // Exact match
    if (n1 === n2) {
        return 1;
    }
    
    // Remove spaces for comparison (e.g., "Gabe Newell" matches "GabeNewell")
    const noSpaces1 = n1.replace(/\s+/g, '');
    const noSpaces2 = n2.replace(/\s+/g, '');
    
    if (noSpaces1 === noSpaces2) {
        return 0.95;
    }
    
    // Check for common smurf patterns: removing numbers, underscores, dashes
    const cleanName = (name: string) => name.replace(/[\s_\-\d]+/g, '');
    const clean1 = cleanName(n1);
    const clean2 = cleanName(n2);
    
    if (clean1 && clean2 && clean1.length > 2 && clean2.length > 2) {
        // Exact match after cleaning
        if (clean1 === clean2) {
            return 0.9;
        }
        
        // Check if one cleaned name contains the other (for variants like "GabeNewell" and "Gabe")
        if (clean1.includes(clean2) || clean2.includes(clean1)) {
            return 0.65 + 0.2 * Math.min(clean1.length, clean2.length) / Math.max(clean1.length, clean2.length);
        }
    }
    
    return 0;
}

/**
 * Check if a friend is potentially a smurf account of the main profile.
 * Currently compares names, but can be extended to compare other attributes.
 * 
 * @param friend - The friend data to check
 * @param mainProfile - The main profile to compare against
 * @returns true if the friend is potentially a smurf
 */
export function getSmurfMatchType(friend: FriendData, mainProfile: ProfileSummary): SmurfMatchType {
    return getSmurfMatch(friend, mainProfile)?.type ?? null;
}

export function getSmurfMatch(friend: FriendData, mainProfile: ProfileSummary): SmurfMatch | null {
    const directStrength = getNameSimilarity(friend.player.personaname, mainProfile.persona);
    const minimumDirectStrength = 0.85;
    if (directStrength >= minimumDirectStrength) {
        return {type: 'direct', strength: directStrength};
    }

    const recentStrength = (mainProfile.recentNames || []).reduce((best, name) =>
        Math.max(best, getNameSimilarity(friend.player.personaname, name)), 0);
    if (recentStrength > directStrength && recentStrength > 0) {
        return {type: 'recent', strength: recentStrength};
    }

    return directStrength > 0 ? {type: 'direct', strength: directStrength} : null;
}

export function isPotentialSmurf(friend: FriendData, mainProfile: ProfileSummary): boolean {
    return getSmurfMatchType(friend, mainProfile) !== null;
}
